/**
 * One deal's product, LTV and maturity, resolved the way the Pipeline grid
 * resolves them, for every screen that prints them.
 *
 * Asset Management, the Surveillance watchlist, Closing and Committee read the
 * raw `loan.ltv`, `loan.product` and `loan.maturity`. GET /loans sends no
 * `product` (the capital program IS the product), the deal book states its
 * leverage as one "In Place LTV/LTC" figure rather than `ltv`, and an imported
 * Funded deal carries its funded date and term but no stored maturity. So on
 * the production replica all 36 funded loans printed product, LTV and maturity
 * blank while the Pipeline showed them (2 Nov 2026 hardening pass).
 *
 * Settled client definitions (Edward / Chris Young, 26 Oct to 2 Nov 2026):
 * - LTV-else-LTC: the in-place LTV when stated, else the LTC, else the deal
 *   book's single In Place LTV/LTC figure — each labeled by what it is.
 * - Maturity = closing (funded) date + initial term months; Extension Maturity =
 *   closing + initial term + Extns (the extension months). A stated maturity is
 *   a statement and wins over the formula (`backend/app/underwriting/dealfields.py`).
 *
 * Absent stays absent: every reader returns null when the deal does not say.
 */
import type { Loan } from '@/types'
import { deskDateIso } from './deskTime'
import { presentNumber, pctRecorded } from './helpers'
import { customValue, loanExtensionMonths, loanInPlaceLtvLtc, loanLtc, loanTermMonths } from './loanFigures'
import { LTV_LTC_ELSE, ltvLtcDisplay } from './ltvLtcDisplay'

type ResolvedLoan = Pick<Loan, 'customFields' | 'ltv' | 'capitalProgram' | 'product' | 'fundedAt' | 'maturity'>

/** The lending program the deal sits in: capital_program, as the grid and the dossier read it. */
export function loanCapitalProgram(loan: Pick<Loan, 'capitalProgram' | 'product'>): string | null {
  const program = typeof loan.capitalProgram === 'string' ? loan.capitalProgram.trim() : ''
  if (program) return program
  const product = typeof loan.product === 'string' ? loan.product.trim() : ''
  return product || null
}

/** What a resolved leverage figure is: the LTV, the LTC, or the book's one In Place LTV/LTC. */
export type LtvLtcBasis = 'ltv' | 'ltc' | 'ltvLtc'

export interface LtvLtcReading {
  /** A fraction (0.65 is 65%), or null when the deal states no leverage. */
  value: number | null
  basis: LtvLtcBasis | null
}

/** The one leverage figure LTV-else-LTC shows (the grid's In Place LTV/LTC column). */
export function loanLtvLtc(loan: Pick<Loan, 'customFields' | 'ltv'>): LtvLtcReading {
  const ltv = presentNumber(loan.ltv)
  if (ltv != null) return { value: ltv, basis: 'ltv' }
  const ltc = loanLtc(loan)
  if (ltc != null) return { value: ltc, basis: 'ltc' }
  const stated = loanInPlaceLtvLtc(loan)
  if (stated != null) return { value: stated, basis: 'ltvLtc' }
  return { value: null, basis: null }
}

/** In Place LTV/LTC as the grid prints it (`view_grid._ltv_ltc_cell` is the
 *  server twin). `ltv_else_ltc` (default): the in-place LTV when present, else
 *  LTC, marked which ("65.0% LTC"). `both`: "67.7% / 72.1%". */
export function ltvLtcText(loan: Pick<Loan, 'customFields' | 'ltv'>, decimals = 1): string | null {
  const ltv = presentNumber(loan.ltv)
  const ltc = loanLtc(loan)
  if (ltv == null && ltc == null) {
    // A deal book's "In Place LTV/LTC" is one figure, kept as the sheet states
    // it: printed until the deal states its own LTV or LTC.
    const stated = loanInPlaceLtvLtc(loan)
    return stated == null ? null : pctRecorded(stated, decimals)
  }
  if (ltvLtcDisplay() === LTV_LTC_ELSE) {
    if (ltv != null) return `${pctRecorded(ltv, decimals)} LTV`
    return `${pctRecorded(ltc, decimals)} LTC`
  }
  return `${pctRecorded(ltv, decimals)} / ${pctRecorded(ltc, decimals)}`
}

/** A calendar day ("YYYY-MM-DD") from a stored date or timestamp, on the desk's clock. */
function calendarDay(raw: unknown): string | null {
  const day = deskDateIso(raw)
  return day && /^\d{4}-\d{2}-\d{2}$/.test(day) ? day : null
}

/** The loan's closing date: the stated closing date, else the day it funded. Never Target Close. */
export function loanClosingDate(loan: Pick<Loan, 'customFields' | 'fundedAt'>): string | null {
  return calendarDay(customValue(loan, 'closing_date')) ?? calendarDay(loan.fundedAt)
}

/** Whole calendar months, or null for a count that is not one. */
function wholeMonths(n: number | null | undefined): number | null {
  if (n == null || !Number.isFinite(n) || n < 1 || !Number.isInteger(n)) return null
  return n
}

/** `day` plus `months` calendar months, the day clamped to the target month's
 *  length (`app.underwriting.calendar_math.add_months`). */
export function addCalendarMonths(day: string, months: number): string {
  const [y, m, d] = day.split('-').map(Number)
  const index = y * 12 + (m - 1) + months
  const year = Math.floor(index / 12)
  const month = index - year * 12
  const last = new Date(Date.UTC(year, month + 1, 0)).getUTCDate()
  const date = Math.min(d, last)
  return `${String(year).padStart(4, '0')}-${String(month + 1).padStart(2, '0')}-${String(date).padStart(2, '0')}`
}

export type MaturityBasis = 'stated' | 'closing+term' | 'closing+term+extensions'

export interface MaturityReading {
  date: string | null
  basis: MaturityBasis | null
}

const NO_DATE: MaturityReading = { date: null, basis: null }

/** Maturity: the stated maturity, else closing date + initial term months. */
export function loanMaturity(loan: ResolvedLoan): MaturityReading {
  const stated = calendarDay(customValue(loan, 'maturity_date')) ?? calendarDay(loan.maturity)
  if (stated) return { date: stated, basis: 'stated' }
  const closing = loanClosingDate(loan)
  const term = wholeMonths(loanTermMonths(loan).num)
  if (!closing || term == null) return NO_DATE
  return { date: addCalendarMonths(closing, term), basis: 'closing+term' }
}

/** Extension Maturity: the stated date, else closing + initial term + Extns
 *  (the extension months, times the option count where the deal records one). */
export function loanExtensionMaturity(loan: ResolvedLoan): MaturityReading {
  const stated = calendarDay(customValue(loan, 'extension_maturity_date'))
  if (stated) return { date: stated, basis: 'stated' }
  const closing = loanClosingDate(loan)
  const term = wholeMonths(loanTermMonths(loan).num)
  const months = wholeMonths(loanExtensionMonths(loan).num)
  if (!closing || term == null || months == null) return NO_DATE
  const countRaw = Number(customValue(loan, 'extension_count'))
  const count = wholeMonths(Number.isFinite(countRaw) ? countRaw : null) ?? 1
  return { date: addCalendarMonths(closing, term + count * months), basis: 'closing+term+extensions' }
}

/** A short hover note for a resolved date: how the platform arrived at it. */
export function maturityBasisNote(reading: MaturityReading): string | undefined {
  switch (reading.basis) {
    case 'stated':
      return 'As stated on the deal'
    case 'closing+term':
      return 'Derived: closing date + initial term'
    case 'closing+term+extensions':
      return 'Derived: closing date + initial term + extensions'
    default:
      return undefined
  }
}
